import React, { useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import WorkWithMeModal from './WorkWithMeModal';
import { Reveal } from './Reveal';

const WorkWithMeSection = () => {
  const { t, language } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ['-12%', '12%']);

  return (
    <section
      id="work-with-me"
      ref={sectionRef}
      className="relative overflow-hidden bg-text-primary"
      data-testid="work-with-me-section"
    >
      {/* Parallax image */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0">
        <img
          src="/img/workwithme.webp"
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="w-full h-[120%] object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-text-primary/85 via-text-primary/70 to-text-primary/95" />
      </motion.div>

      <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 py-32 md:py-48">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          {/* Left — overline & subtitle */}
          <div className="lg:col-span-5">
            <Reveal as="p" className="font-body text-[0.72rem] uppercase tracking-[0.32em] text-accent mb-6">
              {language === 'hu' ? '03 — Együttműködés' : '03 — Collaborations'}
            </Reveal>
            <Reveal as="p" delay={0.1} className="font-heading italic text-2xl md:text-3xl text-background/80 leading-snug max-w-md">
              {t.workWithMe.subtitle}
            </Reveal>
          </div>

          {/* Right — paragraph */}
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal as="p" delay={0.2} className="font-body text-base md:text-lg text-background/70 leading-relaxed max-w-lg">
              {t.workWithMe.description}
            </Reveal>
          </div>
        </div>

        {/* Massive headline */}
        <Reveal as="h2" duration={1.2} delay={0.15} className="mt-16 md:mt-24 font-heading text-background leading-[0.92] tracking-tighter text-[14vw] md:text-[10vw] lg:text-[8.5vw]">
          <span className="block font-extralight">{language === 'hu' ? 'Építsünk' : 'Let\'s build'}</span>
          <span className="block italic font-light">
            {language === 'hu' ? 'együtt valamit' : 'something'}
            <span className="text-accent">.</span>
          </span>
        </Reveal>

        {/* CTA row */}
        <Reveal delay={0.35} className="mt-14 md:mt-20 flex flex-wrap items-center gap-8">
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn-magnetic group inline-flex items-center gap-4 bg-background text-text-primary pl-8 pr-3 py-3.5 rounded-full hover:bg-accent hover:text-background transition-colors duration-700"
            data-testid="work-with-me-cta"
          >
            <span className="font-body text-[0.78rem] uppercase tracking-[0.25em]">{t.workWithMe.cta}</span>
            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-text-primary text-background group-hover:rotate-45 group-hover:bg-background group-hover:text-accent transition-all duration-700">
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </span>
          </button>

          <div className="flex items-center gap-3 text-background/60">
            <span className="block w-10 h-px bg-background/40" />
            <span className="font-body text-[0.72rem] uppercase tracking-[0.3em]">
              {language === 'hu' ? '8 lépés · 2 perc' : '8 steps · 2 minutes'}
            </span>
          </div>
        </Reveal>
      </div>

      <WorkWithMeModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
};

export default WorkWithMeSection;
